import { translate, charsetEscape } from '../utils/translate';
import { log, logMethodCall } from '../utils/debug';
import { addPixelToStyle } from '../utils/addPixelToStyle';
import Event from '../const/event';
import ToastAlert from '../toastAlert';
import { device, os } from '../utils/env';
import { fadeIn, fadeOut } from '../utils/dom';
import { getMessengerParam } from '../utils/getMessengerParams';
import {
  ANDROID_ICON_PATH,
  FACEBOOK_ICON_PATH,
  IOS_ICON_PATH,
  TELEGRAM_ICON_PATH
} from '../const/icons';
import { emailEnvelopSvg } from '../utils/icons';
import CleverPushApi from '../api';
import { getRandomTestVariant } from '../utils/randomTestVariant';

const MESSENGER_WIDGET_KEY = 'messenger-widget';
const MESSENGER_PLATFORM_KEYS = {
  TELEGRAM: 'telegramChannel',
  FACEBOOK: 'facebookChannel',
};
const DEFAULT_TIME_TO_SHOW_TOAST = 5000;
const EMAIL_FADE_DELAY = 100;
const ICON_RESIZE_DELAY = 120;
const CHANNEL_TYPES = {
  Telegram: 'telegram',
  Web: 'web',
  App: 'app',
  Facebook: 'facebook',
  Email: 'email'
};

const TRACK_WIDGET_TYPES = {
  CONFIRM_ALERT: 'confirm-alert',
  OPT_IN: 'optIn',
  OPT_OUT: 'optOut'
};

const SHARED_WIDGET_CHANNEL_TYPES = ['telegram', 'app', 'facebook', 'email'];

const POPUP = 'popup';
const OPT_OUT = 'optOut';
const STATIC_BELL_ICON = 'https://static.cleverpush.com/sdk/assets/bell.svg';

export default class Widget {
  constructor(id, element, config, subscriptionManager, api, triggerEvent) {
    this.element = element;
    if (!this.element) {
      return;
    }
    this.config = config;
    this.subscriptionManager = subscriptionManager;
    this.isSubscribed = this.subscriptionManager && !this.isMessengerWidget(MESSENGER_PLATFORM_KEYS.TELEGRAM) && !this.isMessengerWidget(MESSENGER_PLATFORM_KEYS.FACEBOOK) ? this.subscriptionManager?.subscribed : false;
    this.isSubscribedTargeting = false;
    this.api = api;
    if (this.config.apiEndpoint) {
      this.api.endpoint = this.config.apiEndpoint;
    }
    this.triggerEvent = triggerEvent;
    this.showTopics = true;

    this.elementConfig = this.element.dataset || {};
    this.isWidget = this.element.className === 'cleverpush-widget';
    if (this.isWidget) {
      const widgetConfig = this.config.widgets.find((w) => w._id === this.elementConfig.id) || {};
      this.elementConfig = {
        id: this.elementConfig.id,
        widgetType: this.elementConfig?.widgetType,
        ...widgetConfig,
        type: 'widget',
      };
      this.elementConfig = getRandomTestVariant(this.elementConfig);
      if (this.elementConfig.testId) {
        this.testId = this.elementConfig.testId;
      }
      if (this.element.dataset?.displayType) {
        this.elementConfig.displayType = this.element.dataset.displayType;
      }
    } else {
      this.elementConfig.showTopics = true;
    }

    this.id = this.elementConfig.id || id;

    require('./widget.scss');

    this.channelTopics = (this.config.channelTopics || []).filter((topic) => {
      let show = true;
      if (show && topic.matchPath && topic.matchPath.length) {
        show = new RegExp(topic.matchPath).test(window.location.pathname);
      }
      if (show && topic.notMatchPath && topic.notMatchPath.length) {
        show = !(new RegExp(topic.notMatchPath).test(window.location.pathname));
      }
      return show;
    });

    this.facebookEnabled = false;
    if (this.config.facebookPageId && this.config.facebookPageId.length) {
      this.facebookEnabled = true;

      // generate an unique user reference
      this.facebookUserRef = this.subscriptionManager?.storageManager.getFacebookUserRef();
      if (!this.facebookUserRef) {
        this.facebookUserRef = `${this.config.channelId}-`;
        this.facebookUserRef += Math.random().toString(36).slice(2);
      }
    }

    if (this.elementConfig?.customCss) {
      const node = document.createElement('style');
      node.innerHTML = this.elementConfig.customCss;
      document.body.appendChild(node);
    }
  }

  appendEmailOptIn(configField, widget, emailOptInFormElement, getDefaultEmailOptinForm, subscribeEmailChannel, channel) {
    const importEmailScript = () => {
      window.cleverPushEmailPreventInit = true;
      import('../cleverpush-email').then(({ default: cleverPushEmail }) => {
        const emailChannelId = channel._id;
        cleverPushEmail.channelId = emailChannelId;
        cleverPushEmail.api.channelId = emailChannelId;
        window.cleverPushEmail = cleverPushEmail;
        cleverPushEmail.loadConfig().then(() => {
          const form = cleverPushEmail.config.emailOptInForms?.find((optInForm) => optInForm._id === this.elementConfig.emailOptInForm);
          cleverPushEmail.initForm(emailOptInFormElement, form, this.elementConfig);

          setTimeout(() => {
            this.setWidgetIconHeightAndWidth();
          }, ICON_RESIZE_DELAY);
        });
      });
    };

    if (configField && this.elementConfig.emailOptInForm && widget) {
      this.widgetContentEmailWrap.appendChild(emailOptInFormElement);
      emailOptInFormElement.dataset.id = this.elementConfig.emailOptInForm;
      importEmailScript();
    } else if (widget) {
      emailOptInFormElement.innerHTML = getDefaultEmailOptinForm();
      this.widgetContentEmailWrap.appendChild(emailOptInFormElement);
      emailOptInFormElement.dataset.id = this.elementConfig.emailOptInForm;
      importEmailScript();
      const form = this.element.querySelector('#cleverpush-email-form');
      if (!form) {
        return;
      }
      form.querySelector('fieldset').setAttribute('style', 'margin: 0 !important; padding: 0 !important; border: none !important;');
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        const emailElement = this.element.querySelector('.cleverpush-widget-email-field');
        if (!emailElement.value) {
          return;
        }
        form.querySelector('fieldset').disabled = true;
        subscribeEmailChannel(emailElement.value, form, channel._id, emailOptInFormElement);
      });
    }
  }

  getEmailWidgetConfig(channel) {
    const widget = this.element.querySelector('.cleverpush-widget-content');
    widget.style.minHeight = `${widget.offsetHeight}px`;

    const buttonsAndTopicsElement = this.element.querySelectorAll('.cleverpush-content-button-topics, .cleverpush-widget-buttons');
    [...buttonsAndTopicsElement].forEach((element) => {
      fadeOut(element, EMAIL_FADE_DELAY, () => {
        element.style.display = 'none';
      });
    });
    return { widget, buttonsAndTopicsElement };
  }

  trackShown() {
    try {
      const widgetViewed = parseInt(sessionStorage.getItem(`cleverpush-widget-viewed-${this.elementConfig.id}`), 10);
      const trackingAllowed = 1000 * 60 * 60 * 12; // 12 h
      if (isNaN(widgetViewed) || !widgetViewed || (widgetViewed + trackingAllowed < Date.now())) {
        if (this.config.env !== 'PREVIEW') {
          this.api.trackWidget(this.elementConfig.id, TRACK_WIDGET_TYPES.CONFIRM_ALERT, this.testId);
        }
        sessionStorage.setItem(`cleverpush-widget-viewed-${this.elementConfig.id}`, `${Date.now()}`);
      }
    } catch (e) {
      log.debug(e);
    }
  }

  getSelectedTopics() {
    logMethodCall('getSelectedTopics');

    let topics = (this.channelTopics || []).map((topic) => topic._id);

    if (this.element) {
      const checkboxes = this.element.querySelectorAll('input[name="topics[]"]');
      topics = [];
      for (let i = 0; i < checkboxes.length; i += 1) {
        if ((checkboxes[i].type === 'checkbox' || checkboxes[i].type === 'radio') && checkboxes[i].checked) {
          topics.push(checkboxes[i].value);
        }
      }
    }

    return topics;
  }

  getSelectedTags() {
    logMethodCall('getSelectedTopics');

    let topics = (this.channelTopics || []).map((topic) => topic._id);

    if (this.element) {
      const checkboxes = this.element.querySelectorAll('input[name="tags[]"]');
      topics = [];
      for (let i = 0; i < checkboxes.length; i += 1) {
        if ((checkboxes[i].type === 'checkbox') && checkboxes[i].checked) {
          topics.push(checkboxes[i].value);
        }
      }
    }
    return topics;
  }

  setWidgetIconHeightAndWidth = () => {
    const iconElement = this.element.querySelector('.cleverpush-widget-icon');
    const iconElementImg = this.element.querySelector('.cleverpush-widget-icon img');

    if (!iconElement || !iconElementImg) {
      return;
    }

    const setHeight = () => {
      let content = this.element.querySelector('.cleverpush-widget-content');
      const contentEmail = this.element.querySelector('.cleverpush-widget-content-email');
      if (contentEmail && contentEmail.style.display !== 'none') {
        const contentEmailForm = this.element.querySelector('.cleverpush-email-form form');
        if (contentEmailForm) {
          content = contentEmailForm;
        }
      }

      const isMobile = device.type === 'mobile' || this.config.previewType === 'mobile';

      const contentElementHeight = content?.clientHeight;
      if (iconElement && this.elementConfig.iconFullHeight && !isMobile) {
        iconElement.style.height = `${contentElementHeight}px`;

        if (!this.elementConfig.iconStyle?.width) {
          const aspectRatio = iconElementImg.naturalWidth / iconElementImg.naturalHeight;
          const calculatedWidth = contentElementHeight * aspectRatio;
          iconElement.style.width = `${calculatedWidth}px`;
          iconElement.style.minWidth = `${calculatedWidth}px`;
          iconElement.style.maxWidth = `${calculatedWidth}px`;
        }
      }

      if (isMobile && this.elementConfig.iconFullHeight) {
        iconElement.style.width = '100%';
        iconElement.style.marginRight = '0';
        iconElementImg.style.width = '100%';
      }
    };

    const isLoaded = iconElementImg.complete && iconElementImg.naturalHeight !== 0;
    if (isLoaded) {
      setHeight();
      return;
    }

    iconElementImg.addEventListener('load', setHeight);
  }

  getBrandingLinks() {
    const links = [];

    if (!this.config.hideBranding) {
      links.push(`<a href="https://cleverpush.com/" target="_blank" style="${this.config.confirmAlertThemeLinkColor ? (`color: ${this.config.confirmAlertThemeLinkColor} !important`) : ''}">Powered by CleverPush</a>`);
    }
    if (this.config.privacyPolicyUrl) {
      links.push(`<a href="${this.config.privacyPolicyUrl}" target="_blank" style="${this.config.confirmAlertThemeLinkColor ? (`color: ${this.config.confirmAlertThemeLinkColor} !important`) : ''}">${this.config.privacyPolicyText || translate('privacy')}</a>`);
    }
    if (this.config.faqText && this.config.faqUrl) {
      links.push(`<a href="${this.config.faqUrl}" target="_blank" style="${this.config.confirmAlertThemeLinkColor ? (`color: ${this.config.confirmAlertThemeLinkColor} !important`) : ''}">${this.config.faqText || 'FAQ'}</a>`);
    }

    return links.join(' &nbsp;/&nbsp; ');
  }

  show(isSubscribedParam) {
    (async () => {
      try {
        if (!this.isMessengerWidget(MESSENGER_PLATFORM_KEYS.TELEGRAM) && !this.isMessengerWidget(MESSENGER_PLATFORM_KEYS.FACEBOOK)) {
          this.isSubscribed = isSubscribedParam;
          this.isSubscribedTargeting = isSubscribedParam;
        }

        if (this.isSubscribedTargeting && this.elementConfig.buttonOnlyForTagsTopics) {
          if (this.elementConfig.assignTags && this.elementConfig.tags && this.elementConfig.tags.length) {
            // eslint-disable-next-line no-restricted-syntax
            for (const tagId of this.elementConfig.tags) {
              // eslint-disable-next-line no-await-in-loop
              const hasTag = await this.subscriptionManager?.storageManager.hasTag(tagId);
              if (!hasTag) {
                this.isSubscribedTargeting = false;
                break;
              }
            }
          }
          if (this.isSubscribedTargeting && this.elementConfig.assignTopics && this.elementConfig.topics && this.elementConfig.topics.length) {
            const topicIds = await this.subscriptionManager?.getTopics();
            // eslint-disable-next-line no-restricted-syntax
            for (const topicId of this.elementConfig.topics) {
              // eslint-disable-next-line no-await-in-loop
              const hasTopic = topicIds.includes(topicId);
              if (!hasTopic) {
                this.isSubscribedTargeting = false;
                break;
              }
            }
          }
        }

        const isSubscribed = this.elementConfig.buttonOnlyForTagsTopics ? this.isSubscribedTargeting : this.isSubscribed;

        if (this.isWidget) {
          try {
            if (localStorage.getItem(`cleverpush-widget-status-${this.elementConfig.id}`) === 'hidden') {
              return;
            }
          } catch (ignored) { }

          if (!this.elementConfig.showSubscribed && isSubscribed && !this.elementConfig.emailOptInFormVisibleByDefault) {
            if (this.element) {
              this.element.style.setProperty('display', 'none', 'important');
            }
            return;
          }
          if (this.element) {
            this.element.style.display = '';
          }
        }

        const getDefaultEmailOptinForm = () => '<form id="cleverpush-email-form"><fieldset><div class="cleverpush-email-form-content"><input type="email" placeholder="E-Mail-Addrese eintragen" class="cleverpush-widget-email-field" name="email"><button class="cleverpush-widget-email-button" type="submit">jetzt zum newsletter anmelden</button></div></fieldset></form>';

        let setElementHtml;

        const onMultiChannelAppClick = (channel) => {
          if (os.name === 'Android') {
            if (this.config.env !== 'PREVIEW') {
              this.api.trackWidget(this.elementConfig.id, TRACK_WIDGET_TYPES.OPT_IN, this.testId, CHANNEL_TYPES.App);
            }
            window.open(`https://play.google.com/store/apps/details?id=${channel.androidPackageName}`);
          }
          if (os.name === 'iOS') {
            this.api.trackWidget(this.elementConfig.id, TRACK_WIDGET_TYPES.OPT_IN, this.testId, CHANNEL_TYPES.App);
            window.open(`https://apps.apple.com/us/app/cleverpush/id${channel.iosStoreId}`);
          }
        };

        const onMultiChannelTelegramClick = (channel) => {
          const telegramUsername = channel.telegramUsername;
          if (this.config.env !== 'PREVIEW') {
            this.api.trackWidget(this.elementConfig.id, TRACK_WIDGET_TYPES.OPT_IN, this.testId, CHANNEL_TYPES.Telegram);
          }
          window.open(`https://t.me/${telegramUsername}?start=${getMessengerParam()}`);
        };

        const onMultiChannelFacebookClick = (channel) => {
          const facebookPageId = channel.facebookPage.id;
          if (this.config.env !== 'PREVIEW') {
            this.api.trackWidget(this.elementConfig.id, TRACK_WIDGET_TYPES.OPT_IN, this.testId, CHANNEL_TYPES.Facebook);
          }
          window.open(`https://m.me/${facebookPageId}?ref=${getMessengerParam()}`);
        };

        const renderWidgetContent = (topicSelection, tagSelection) => {
          let optOutTextElement = '';
          let optOutText = (this.elementConfig.showOptOutSettings ? this.elementConfig.optOutText : null) || this.elementConfig.text;
          if (this.elementConfig.emailOptInFormVisibleByDefault) {
            optOutText = '';
          }
          if (optOutText) {
            optOutTextElement = `
              <div class="cleverpush-widget-text cleverpush-widget-optOut-text">
                ${optOutText}
              </div>`;
          }

          let optOutTitleElement = '';
          let optOutTitle = this.elementConfig.optOutTitle || this.elementConfig.title;
          if (this.elementConfig.emailOptInFormVisibleByDefault) {
            optOutTitle = '';
          }
          if (optOutTitle) {
            optOutTitleElement = `
              <div class="cleverpush-widget-title cleverpush-widget-optOut-title">
                ${optOutTitle}
              </div>`;
          }

          let optInTitle = this.elementConfig.title || '';
          let optInText = this.elementConfig.text || '';
          let optInSubtitle = this.elementConfig.subtitle || '';

          if (this.elementConfig.emailOptInFormVisibleByDefault) {
            optInTitle = '';
            optInText = '';
            optInSubtitle = '';
          }

          const widget = `
          ${this.elementConfig.displayType === POPUP ? (`
            <div class="cleverpush-widget-popup-content">
              <button type="button" class="cleverpush-widget-close-button">&times;</button>`) : ''}

              ${this.elementConfig.showIcon ? (`
              <div class="cleverpush-widget-icon">
                ${this.elementConfig.iconUrl ? (`<img src="${this.elementConfig.iconUrl || ''}" style="max-width: 100%; object-fit: cover;" />`) : (`<img src="${STATIC_BELL_ICON}" style="opacity: 0.25; max-width: 100%; height: 100%; width: 100%;" />`)}
              </div>
              `) : ''}

              <div class="cleverpush-widget-content">

              ${this.elementConfig.showContent ? (`
                ${!isSubscribed && this.elementConfig.previewTab !== OPT_OUT && !this.elementConfig.emailOptInFormVisibleByDefault ? (`
                <div class="cleverpush-widget-title">
                  ${optInTitle}
                </div>
                `) : `${optOutTitleElement}`}
              `) : ''}

                ${this.elementConfig.showSubtitle && this.elementConfig.subtitle && !isSubscribed && this.elementConfig.previewTab !== OPT_OUT && !this.elementConfig.emailOptInFormVisibleByDefault ? (`
                <div class="cleverpush-widget-subtitle">
                  ${optInSubtitle}
                </div>
                `) : ''}

                ${this.elementConfig.showContent ? (`
                  ${!isSubscribed && this.elementConfig.previewTab !== OPT_OUT && !this.elementConfig.emailOptInFormVisibleByDefault ? (`
                    <div class="cleverpush-widget-text cleverpush-widget-description-text">
                      ${optInText}
                    </div>
                    `) : `${optOutTextElement}`}
                `) : ''}

                ${this.elementConfig.showTopics ? (`
                <div class="cleverpush-widget-text" style="margin-top: 15px; margin-bottom: 15px;">
                  ${topicSelection}
                </div>`) : ''}

                ${this.elementConfig.showTags ? (`
                <div class="cleverpush-widget-text" style="margin-top: 15px; margin-bottom: 15px;">
                  ${tagSelection}
                </div>`) : ''}
                <div class="cleverpush-widget-buttons" style="${this.elementConfig.emailOptInFormEnabled && this.elementConfig.emailOptInFormVisibleByDefault ? 'display: none;' : ''}">
                  ${this.elementConfig.showDenyButton && !isSubscribed ? (`
                  <button type="button" class="cleverpush-widget-deny-button">
                    ${this.elementConfig.denyButtonText || 'Ablehnen'}
                  </button>`) : ''}
                  
                  ${(isSubscribed || this.elementConfig.previewTab === OPT_OUT) && !this.elementConfig.hideUnsubscribeButton && !this.isMessengerWidget(MESSENGER_PLATFORM_KEYS.TELEGRAM) && !this.isMessengerWidget(MESSENGER_PLATFORM_KEYS.FACEBOOK) ? `
                  <button type="button" class="cleverpush-widget-unsubscribe-button">
                    ${this.elementConfig.denyButtonText || this.elementConfig.unsubscribeButtonText || 'Push-Nachrichten deaktivieren'}
                  </button>
                  ` : !this.elementConfig.hideDefaultSubscribeButton ? `
                  <button type="button" class="cleverpush-widget-allow-button">
                    ${this.elementConfig.allowButtonText || 'Akzeptieren'}
                  </button>` : ''}

                ${this.getMultiWidgetButtons()}
                </div>
              </div>

          ${this.elementConfig.displayType === POPUP ? (`
            </div >`) : ''}`;
          return widget;
        };

        const subscribeEmailChannel = (email, form, channelId, emailOptinFormElement) => {
          const data = {
            emailAddress: email,
            connectedSubscriptionId: '',
            optInUrl: location.href,
            sdkVersion: '1.0.0',
            widgetId: this.elementConfig.id,
            widgetChannelId: this.config.channelId,
            widgetTestId: this.testId
          };
          this.api.request('POST', `/subscription/sync/${channelId}`, data).then((json) => new Promise((resolve, reject) => {
            form.querySelector('fieldset').disabled = false;
            if (json && json.id) {
              resolve(json);
            } else if (json && json.error) {
              reject(json.error);
            } else {
              reject();
            }
          })).then((res) => {
            const defaultContent = `<div class="cleverpush-email-overlay">
          <h1>Vielen Dank</h1>
          <p>Wir bedanken uns für Ihre Anmeldung.</p>
          </div>`;

            emailOptinFormElement.querySelector('.cleverpush-email-form-content').style.opacity = '0.2';
            form.innerHTML += defaultContent;
          });
        };

        const onMultiChannelEmailClick = (channel) => {
          const { widget, buttonsAndTopicsElement } = this.getEmailWidgetConfig(channel);
          if (!this.widgetContentEmailWrap) {
            this.widgetContentEmailWrap = document.createElement('div');
            this.widgetContentEmailWrap.className = 'cleverpush-widget-content-email';
            const emailOptInFormElement = document.createElement('div');
            emailOptInFormElement.className = 'cleverpush-email-form';
            emailOptInFormElement.style.width = '100%';
            widget.appendChild(this.widgetContentEmailWrap);

            this.appendEmailOptIn(this.elementConfig.emailOptInFormEnabled, widget, emailOptInFormElement, getDefaultEmailOptinForm, subscribeEmailChannel, channel);

            const widgetEmailBackButton = document.createElement('button');
            widgetEmailBackButton.type = 'button';
            widgetEmailBackButton.addEventListener('click', () => {
              buttonsAndTopicsElement.forEach((element) => {
                fadeOut(this.widgetContentEmailWrap, EMAIL_FADE_DELAY, () => {
                  const hasEmailIcon = this.widgetContentEmailWrap.querySelector('.cleverpush-email-form-icon') != null;
                  this.widgetContentEmailWrap.style.display = 'none';
                  element.style.display = '';
                  fadeIn(element, EMAIL_FADE_DELAY);
                  this.setWidgetIconHeightAndWidth();

                  if (hasEmailIcon) {
                    const iconElement = this.element.querySelector('.cleverpush-widget-icon');
                    iconElement.style.display = 'block';
                  }
                });
              });
            });
            widgetEmailBackButton.innerText = 'Zurück';
            widgetEmailBackButton.className = 'cleverpush-widget-email-back';
            this.widgetContentEmailWrap.appendChild(widgetEmailBackButton);
          }

          setTimeout(() => {
            this.widgetContentEmailWrap.style.opacity = '0';
            this.widgetContentEmailWrap.style.display = '';
            const hasEmailIcon = this.widgetContentEmailWrap.querySelector('.cleverpush-email-form-icon') != null;
            if (hasEmailIcon) {
              const iconElement = this.element.querySelector('.cleverpush-widget-icon');
              iconElement.style.display = 'none';
            }
            fadeIn(this.widgetContentEmailWrap, EMAIL_FADE_DELAY);
          }, EMAIL_FADE_DELAY);

          setTimeout(() => {
            this.setWidgetIconHeightAndWidth();
          }, ICON_RESIZE_DELAY);
        };

        const initiateElements = () => {
          const { widgetSharedChannels } = this.elementConfig;
          this.button = this.element.querySelector('.cleverpush-content-button-btn-subscribe, .cleverpush-widget-allow-button');
          if (this.button) {
            this.button.addEventListener('click', this.onClick.bind(this));
            if (this.element && this.element.dataset) {
              if (this.element.dataset.buttonBackgroundColor) {
                this.button.style.backgroundColor = this.element.dataset.buttonBackgroundColor;
              }
              if (this.element.dataset.buttonColor) {
                this.button.style.color = this.element.dataset.buttonColor;
              }
            }
          }

          if (this.elementConfig.displayType === POPUP) {
            this.element.addEventListener('click', (e) => {
              if (e.target === e.currentTarget) {
                this.element.style.display = 'none';
                document.body.classList.remove('cleverpush-widget-popup-open');
              }
            });
          }

          (widgetSharedChannels || []).forEach((channel) => {
            const element = this.element.querySelector(`.cleverpush-widget-allow-button_${channel._id}`);
            if (channel.type === CHANNEL_TYPES.App && element) {
              element.addEventListener('click', () => onMultiChannelAppClick(channel));
            }
            if (channel.type === CHANNEL_TYPES.Facebook && element) {
              element.addEventListener('click', () => onMultiChannelFacebookClick(channel));
            }
            if (channel.type === CHANNEL_TYPES.Telegram && element) {
              element.addEventListener('click', () => onMultiChannelTelegramClick(channel));
            }
            if (channel.type === CHANNEL_TYPES.Email && element) {
              element.addEventListener('click', () => onMultiChannelEmailClick(channel));

              if (this.elementConfig.emailOptInFormVisibleByDefault) {
                const { widget } = this.getEmailWidgetConfig(channel);
                if (!this.widgetContentEmailWrap) {
                  this.widgetContentEmailWrap = document.createElement('div');
                  this.widgetContentEmailWrap.className = 'cleverpush-widget-content-email';

                  const emailOptInFormElement = document.createElement('div');
                  emailOptInFormElement.className = 'cleverpush-email-form';
                  emailOptInFormElement.style.width = '100%';
                  widget.appendChild(this.widgetContentEmailWrap);

                  this.appendEmailOptIn(this.elementConfig.emailOptInFormVisibleByDefault, widget, emailOptInFormElement, getDefaultEmailOptinForm, subscribeEmailChannel, channel);
                } else {
                  widget.appendChild(this.widgetContentEmailWrap);
                }

                this.widgetContentEmailWrap.style.opacity = '0';
                this.widgetContentEmailWrap.style.display = '';
                fadeIn(this.widgetContentEmailWrap, EMAIL_FADE_DELAY);

                setTimeout(() => {
                  this.setWidgetIconHeightAndWidth();
                }, ICON_RESIZE_DELAY);
              }
            }
          });

          this.content = this.element.querySelector('.cleverpush-widget-popup-content');

          if (this.elementConfig.displayType === POPUP) {
            this.element.classList.add('cleverpush-widget-popup');
            document.body.classList.add('cleverpush-widget-popup-open');

            if (this.elementConfig.darkenBackground) {
              this.element.style.backgroundColor = 'rgba(0,0,0,.75)';
            } else {
              this.element.style.pointerEvents = 'none';
              if (this.content) {
                this.content.style.pointerEvents = 'initial';
              }
            }

            if (this.content) {
              this.content.id = `${this.element.className.split(' ')[0]}-${this.id}`;
            }
          } else {
            this.element.id = `${this.element.className.split(' ')[0]}-${this.id}`;
          }

          this.denyButton = this.element.querySelector('.cleverpush-widget-deny-button');
          if (this.denyButton) {
            this.denyButton.addEventListener('click', this.onClickDeny.bind(this));
          }

          this.unsubscribeButton = this.element.querySelector('.cleverpush-content-button-btn-unsubscribe, .cleverpush-widget-unsubscribe-button');
          if (this.unsubscribeButton) {
            this.unsubscribeButton.addEventListener('click', this.onClickUnsubscribe.bind(this));

            if (this.element && this.element.dataset) {
              if (this.element.dataset.buttonBackgroundColor) {
                this.unsubscribeButton.style.setProperty('border-color', this.element.dataset.buttonBackgroundColor, 'important');
              }
            }
          }

          this.closeButton = this.element.querySelector('.cleverpush-widget-close-button');
          if (this.closeButton) {
            this.closeButton.addEventListener('click', this.onClickDeny.bind(this));
          }

          this.topicSelection = this.element.querySelector('.cleverpush-content-button-topics');

          this.saveTopicsButton = this.element.querySelector('.cleverpush-content-button-save-topics-button');
          if (this.saveTopicsButton) {
            if (this.element.dataset.buttonBackgroundColor) {
              this.saveTopicsButton.style.backgroundColor = this.element.dataset.buttonBackgroundColor;
            }
            this.saveTopicsButton.addEventListener('click', this.saveTopics.bind(this));
          }

          this.setWidgetIconHeightAndWidth();

          this.reset(isSubscribed);
        };

        let button = `
          <div class="cleverpush-content-button-btn-wrap">
          <button class="cleverpush-content-button-btn cleverpush-content-button-btn-subscribe">${this.elementConfig.allowButtonText || this.elementConfig.buttonText || translate('bell.subscribe')}</button>
          <button class="cleverpush-content-button-btn cleverpush-content-button-btn-unsubscribe" title="${translate('unsubscribe.title')}" style="display: none;">&times;</button>
          </div>
        `;

        if (this.facebookEnabled) {
          if (window.CleverPush.browserType === 'facebook') {
            button = '';
          }

          if (this.config.facebookDoubleOptIn && !this.facebookDoubleOptInConfirmed) {
            // add the facebook confirm checkbox to topic selection (if double optin enabled)
            button += `
          <div style="margin-top: 15px; margin-bottom: 5px; font-weight: bold;" class="cleverpush-confirm-messenger-title">Facebook Messenger</div>
          <div class="cleverpush-topics cleverpush-confirm-topics"><div><label><input type="${this.config.confirmAlertRadioButtonTopics ? 'radio' : 'checkbox'}" id="cleverpush-facebook-checkbox"> Ich möchte den Facebook-Newsletter und akzeptiere die <a href="${this.config.privacyPolicyUrl}">Datenschutzerklärung</a></label></div></div>`;
          }

          // add the messenger button
          button += `<div class="fb-messenger-checkbox-wrap"><div class="fb-send-to-messenger" origin="${location.origin}" page_id="${this.config.facebookPageId}" messenger_app_id="${this.config.facebookAppId}" user_ref="${this.facebookUserRef}" allow_login="true" size="large" style="margin-top: 10px;"></div></div>`;
        }

        let topicSelection = '';
        let tagSelection = '';
        const showSubscribedTags = this.isSubscribed && this.config.channelTags?.length && this.elementConfig.showSubscribedTags;
        const showTopics = this.channelTopics && this.channelTopics.length && this.elementConfig.showTopics;
        if (showTopics) {
          const selectedTopicIds = await this.subscriptionManager?.getTopics();
          topicSelection += `${showSubscribedTags ? `<h3 class="cleverpush-topic-heading">${translate('widget.topics')}</h3>` : ''}<div class="cleverpush-topics cleverpush-content-button-topics">`;
          this.channelTopics.filter((topic) => !topic.parentTopic).sort((a, b) => a.sort - b.sort).forEach((topic) => {
            const topicName = charsetEscape(topic.name);
            const topicDescription = topic.description ? `<div class="cleverpush-topic-description">${charsetEscape(topic.description)}</div>` : '';
            const childTopics = this.channelTopics.filter((childTopic) => childTopic.parentTopic === topic._id);

            topicSelection += `<label class="cleverpush-input-label"><input type="${this.config.confirmAlertRadioButtonTopics ? 'radio' : 'checkbox'}" name="topics[]" value="${topic._id}"${(!isSubscribed && !topic.defaultUnchecked) || (selectedTopicIds && selectedTopicIds.indexOf(topic._id) > -1) ? ' checked="checked"' : ''}><div>${topicName}${topicDescription}</div></label>`;

            if (childTopics.length) {
              childTopics.forEach((childTopic) => {
                const childTopicName = charsetEscape(childTopic.name);

                topicSelection += `<div class="topic-child" style="margin-left: 20px; font-size: 90%"><label class="cleverpush-input-label"><input type="${this.config.confirmAlertRadioButtonTopics ? 'radio' : 'checkbox'}" name="topics[]" value="${childTopic._id}" ${(!isSubscribed && !childTopic.defaultUnchecked) || (selectedTopicIds && selectedTopicIds.indexOf(childTopic._id) > -1) ? 'checked' : ''}> ${childTopicName}</label></div>`;
              });
            }
          });
        }

        if (showSubscribedTags) {
          let selectedTags = await this.subscriptionManager?.getTags();
          const whitelistTags = this.elementConfig.assignTagsWhitelist;
          if (this.elementConfig.assignTagsWhitelistEnabled && whitelistTags?.length) {
            selectedTags = selectedTags.filter((tag) => whitelistTags.includes(tag));
          }
          if (selectedTags?.length) {
            this.elementConfig.showTags = true;
            const subscriptionTags = this.config.channelTags.filter((tag) => selectedTags.includes(tag._id));
            tagSelection += `<div class="cleverpush-tags"><h3 class="cleverpush-topic-heading">${translate('widget.tags')}</h3>${subscriptionTags.map((tag) => {
              const tagName = charsetEscape(tag.name);
              const tagContent = `<label class="cleverpush-input-label"><input type="checkbox" name="tags[]" value="${tag._id}"checked="checked"><div>${tagName}</div></label>`;
              return tagContent;
            }).join('')}</div>`;
          }
        }

        if (this.elementConfig.showTags) {
          tagSelection += `<button type="button" class="cleverpush-content-button-save-topics-button cleverpush-content-button-btn" style="display: none;">${translate('panel.save')}</button>`;
          tagSelection += '</div>';
        } else if (showTopics) {
          topicSelection += `<button type="button" class="cleverpush-content-button-save-topics-button cleverpush-content-button-btn" style="display: none;">${translate('panel.save')}</button>`;
          topicSelection += '</div>';
        }

        if (this.elementConfig.type === 'widget') {
          setElementHtml = () => {
            const widget = renderWidgetContent(topicSelection, tagSelection);

            const getStyle = (currentStyle) => `
              #${`cleverpush-widget-${this.elementConfig.id}`} { ${addPixelToStyle(currentStyle.style || {})} }
              #${`cleverpush-widget-${this.elementConfig.id}`} .cleverpush-widget-content { ${addPixelToStyle(currentStyle.contentStyle || {})} }
              #${`cleverpush-widget-${this.elementConfig.id}`} .cleverpush-widget-title { ${addPixelToStyle(currentStyle.titleStyle || {})} }
              #${`cleverpush-widget-${this.elementConfig.id}`} .cleverpush-widget-subtitle { ${addPixelToStyle(currentStyle.subtitleStyle || {})} }
              #${`cleverpush-widget-${this.elementConfig.id}`} .cleverpush-widget-buttons { ${addPixelToStyle(currentStyle.buttonsStyle || {})} }
              #${`cleverpush-widget-${this.elementConfig.id}`} .cleverpush-widget-description-text { ${addPixelToStyle(currentStyle.textStyle || {})} }
              #${`cleverpush-widget-${this.elementConfig.id}`} .cleverpush-widget-icon { ${addPixelToStyle(currentStyle.iconStyle || {})} }
              #${`cleverpush-widget-${this.elementConfig.id}`} .cleverpush-widget-icon img { width: ${currentStyle.iconStyle?.width ? `${currentStyle.iconStyle?.width}px` : '100%'}; }
              #${`cleverpush-widget-${this.elementConfig.id}`} .cleverpush-widget-field { ${addPixelToStyle(currentStyle.fieldStyle || {})} }
              #${`cleverpush-widget-${this.elementConfig.id}`} .cleverpush-widget-field label { ${addPixelToStyle(currentStyle.labelStyle || {})} }
              #${`cleverpush-widget-${this.elementConfig.id}`} .cleverpush-widget-allow-button, #${`cleverpush-widget-${this.elementConfig.id}`} .cleverpush-widget-unsubscribe-button, #${`cleverpush-widget-${this.elementConfig.id}`} .cleverpush-content-button-btn { ${addPixelToStyle(currentStyle.allowButtonStyle || {})} }
              #${`cleverpush-widget-${this.elementConfig.id}`} .cleverpush-widget-deny-button { ${addPixelToStyle(currentStyle.denyButtonStyle || {})} }
              `;

            const style = ` 
            ${getStyle(this.elementConfig)}
            ${this.config.previewType === 'mobile' ? getStyle(this.elementConfig?.mobileStyle || this.elementConfig) : ''}
            @media (max-width: 600px) {
              ${getStyle(this.elementConfig?.mobileStyle || this.elementConfig)}
            }`;

            if (!this.styleElem) {
              this.styleElem = document.createElement('style');
              document.head.appendChild(this.styleElem);
            }
            this.styleElem.innerHTML = `${style.replace(/"/g, '\\"').replace(/(\r\n|\n|\r)/gm, '')}`;

            this.element.innerHTML = widget;

            initiateElements();
          };
        } else if (this.elementConfig.type === 'box-simple' || this.elementConfig.type === 'box-count') {
          setElementHtml = () => {
            this.element.innerHTML = (
              `${`<div class="cleverpush-content-button-box cleverpush-content-button-${this.elementConfig.type}">`
              + '<div class="cleverpush-content-button-top">'
              + `  <div class="cleverpush-content-button-icon" style="background-image: url('${this.config.channelIcon}')"></div>`
              + '  <div class="cleverpush-content-button-body">'
              + `    <div class="cleverpush-content-button-title">${this.elementConfig.title || this.config.channelName}</div>`
              + `    <div class="cleverpush-content-button-text">${this.elementConfig.text || translate('confirm.info')}</div>`
              + '  </div>'
              + '</div>'
              + '<div class="cleverpush-content-button-bottom">'}${topicSelection
              }${button
              }  <div class="cleverpush-clearfix"></div>`
              + '</div>'
              + '<div class="cleverpush-content-button-branding">'
              + ` <span>
                ${this.getBrandingLinks()}
              </span>`
              + '</div>'
              + '</div>'
            );

            initiateElements();
          };
        } else {
          setElementHtml = () => {
            this.element.className += ' cleverpush-content-button-simple';
            this.element.innerHTML = button;

            initiateElements();
          };
        }

        setElementHtml();

        const widgetIconElement = this.element.querySelector('.cleverpush-widget-icon img');
        if (widgetIconElement) {
          if (this.elementConfig.mobileIconUrl && (device.type === 'mobile' || this.config.previewType === 'mobile')) {
            widgetIconElement.src = this.elementConfig.mobileIconUrl;
          } else {
            widgetIconElement.src = this.elementConfig.iconUrl || STATIC_BELL_ICON;
          }
        }

        this.triggerEvent(Event.WIDGET_SHOWN);
      } catch (err) {
        console.log('error', err);
      }
    })();
  }

  getMultiWidgetButtons = () => {
    const { multiChannels } = this.config;
    const {
      channelButtonStyle, channelSharingEnabled, widgetSharedChannels,
    } = this.elementConfig;

    let buttons = '';
    let style = '';
    if (!channelSharingEnabled) {
      return '';
    }

    const getSvgIcon = (color, path, viewBox) => `
      <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="${viewBox}"
          width="23px"
          fill="${color}"
          height="23px"
        >
          <path d="${path}" />
        </svg>
    `;

    const getButtonIcons = (type, color = '#fff') => {
      switch (type) {
        case CHANNEL_TYPES.Facebook: {
          return getSvgIcon(color, FACEBOOK_ICON_PATH, '0 0 28 28');
        }

        case CHANNEL_TYPES.Telegram: {
          return getSvgIcon(color, TELEGRAM_ICON_PATH, '0 0 55 55');
        }

        case CHANNEL_TYPES.App: {
          if (os.name === 'Android') {
            return getSvgIcon(color, ANDROID_ICON_PATH, '0 0 52 52');
          }
          if (os.name === 'iOS') {
            return getSvgIcon(color, IOS_ICON_PATH, '0 0 52 52');
          }
          return '';
        }
        case CHANNEL_TYPES.Email: {
          return emailEnvelopSvg(color);
        }

        default:
          return '';
      }
    };

    const addButton = (channelId, text, type, icon, color = '#fff') => (
      `<button type="button" style="margin:5px;min-height:42px;outline:none" class="cleverpush-widget-allow-button_${channelId}">
        <div style="display:flex;align-items:center;justify-content:center">
        <span style="margin-right:3px">${getButtonIcons(type, color)}</span>
          <span>${text}</span>
        </div>
      </button>`
    );

    const addButtonsStyles = (selectedChannelStyle, channelId, type, defaultText, icon) => {
      const buttonText = selectedChannelStyle.buttonText || defaultText;
      buttons += addButton(channelId, buttonText, type, icon, selectedChannelStyle.style?.color);
      style += `.cleverpush-widget-allow-button_${channelId} { ${addPixelToStyle(selectedChannelStyle.style || {})} }`;
    };

    (widgetSharedChannels || []).forEach((channel) => {
      if (channel.type === CHANNEL_TYPES.App) {
        const selectedChannelStyle = channelButtonStyle[channel._id];
        if (os.name === 'Android' && channel.androidPackageName) {
          addButtonsStyles(selectedChannelStyle, channel._id, channel.type, 'Android App', 'android');
        }
        if (os.name === 'iOS' && channel.iosStoreId) {
          addButtonsStyles(selectedChannelStyle, channel._id, channel.type, 'iOS App', 'ios');
        }
      }
      if (channel.type === CHANNEL_TYPES.Facebook && channel.facebookPage && channel.facebookPage.id) {
        const selectedChannelStyle = channelButtonStyle[channel._id];
        addButtonsStyles(selectedChannelStyle, channel._id, channel.type, 'Facebook', 'facebook');
      }
      if (channel.type === CHANNEL_TYPES.Telegram && multiChannels.telegramChannel.telegramUsername) {
        const selectedChannelStyle = channelButtonStyle[channel._id];
        addButtonsStyles(selectedChannelStyle, channel._id, channel.type, 'Telegram', 'telegram');
      }
      if (channel.type === CHANNEL_TYPES.Email) {
        const selectedChannelStyle = channelButtonStyle[channel._id];
        addButtonsStyles(selectedChannelStyle, channel._id, channel.type, 'Newsletter', 'email');
      }
    });
    if (buttons) {
      const styleElem = document.createElement('style');
      document.head.appendChild(styleElem);
      styleElem.innerHTML = `${style.replace(/"/g, '\\"').replace(/(\r\n|\n|\r)/gm, '')}`;
    }
    return buttons;
  };

  reset(isSubscribed) {
    if (this.button) {
      if (isSubscribed && !this.isMessengerWidget(MESSENGER_PLATFORM_KEYS.TELEGRAM) && !this.isMessengerWidget(MESSENGER_PLATFORM_KEYS.FACEBOOK)) {
        this.button.className += ' disabled';
        this.button.innerHTML = this.elementConfig.denyButtonText || this.elementConfig.buttonTextSubscribed || (`✓&nbsp; ${translate('bell.subscribed')}`);
        if (this.element && this.element.dataset && this.element.dataset.buttonBackgroundColor) {
          this.button.style.setProperty('border-color', this.element.dataset.buttonBackgroundColor, 'important');
        }
        if (this.elementConfig.hideUnsubscribeButton) {
          this.button.setAttribute('disabled', '');
          this.button.style.setProperty('cursor', 'no-drop', 'important');
        }
      } else {
        this.button.className.replace(' disabled', '');
        this.button.innerHTML = this.elementConfig.allowButtonText || this.elementConfig.buttonText || translate('bell.subscribe');
        if (this.element && this.element.dataset && this.element.dataset.buttonBackgroundColor) {
          this.button.style.removeProperty('border-color');
        }
        if (this.elementConfig.hideUnsubscribeButton) {
          this.button.removeAttribute('disabled');
          this.button.style.setProperty('cursor', 'pointer', 'important');
        }
      }
    }

    if (this.elementConfig.subscribedHideAllowButton) {
      const allowBtn = this.element.querySelector('.cleverpush-widget-allow-button');
      if (allowBtn) {
        allowBtn.style.display = isSubscribed ? 'none' : '';
      }
    }

    if (this.unsubscribeButton) {
      if (isSubscribed || this.elementConfig.previewTab === OPT_OUT) {
        this.unsubscribeButton.style.display = '';
      } else {
        this.unsubscribeButton.style.display = 'none';
      }
    }

    if (this.topicSelection) {
      this.topicSelection.style.display = isSubscribed && !this.showTopics ? 'none' : '';
    }
    if (this.saveTopicsButton) {
      this.saveTopicsButton.style.display = isSubscribed ? '' : 'none';
    }
  }

  updateTagsPromise(tags, subscriptionId, removeTags) {
    const promises = tags.map((tagId) => new Promise((resolve) => {
      this.api[(this.isSubscribedTargeting || removeTags) ? 'untagSubscription' : 'tagSubscription'](subscriptionId, tagId).then(async () => {
        resolve(tagId);
      });
    }));
    return promises;
  }

  async addOrRemoveTags(tags, subscriptionId, callback, isRemoveTags) {
    return Promise.all(this.updateTagsPromise(tags, subscriptionId, isRemoveTags)).then(async (tagIds) => {
      // eslint-disable-next-line no-restricted-syntax
      for await (const tag of tagIds) {
        const storedTags = await this.subscriptionManager?.storageManager.getTags();
        if (this.isSubscribedTargeting) {
          storedTags.splice(storedTags.indexOf(tag), 1);
        } else {
          storedTags.push(tag);
        }
        await this.subscriptionManager?.storageManager.setTags([...new Set(storedTags)]);
      }
      this.triggerEvent(Event.TAGS_SET);
      if (callback) {
        callback();
      }
    });
  }

  onTargetingClick() {
    const promises = [];

    if (this.elementConfig.assignTags && this.elementConfig.tags && this.elementConfig.tags.length) {
      promises.push(new Promise((resolve) => {
        this.subscriptionManager?.getSubscriptionId().then((subscriptionId) => {
          this.addOrRemoveTags(this.elementConfig.tags, subscriptionId, resolve)
            .then()
            .catch((err) => {
              console.log('Error while updating tags', err);
            });
        });
      }));
    }

    if (this.elementConfig.assignTopics && this.elementConfig.topics && this.elementConfig.topics.length) {
      promises.push(new Promise((resolve) => {
        this.subscriptionManager?.getTopics().then((topics) => {
          const newTopics = [...topics];
          this.elementConfig.topics.forEach((topicId) => {
            const index = newTopics.findIndex((topic) => topic === topicId);
            if (this.isSubscribedTargeting && index >= 0) {
              newTopics.splice(index, 1);
            } else if (!this.isSubscribedTargeting && index < 0) {
              newTopics.push(topicId);
            }
          });
          this.subscriptionManager?.setTopics(newTopics);
          this.subscriptionManager?.sync().then(() => {
            this.triggerEvent(Event.TOPICS_SET);
            resolve();
          });
        });
      }));
    }

    return Promise.all(promises);
  }

  showSuccessMessageAlert = (subscribeMessage) => new ToastAlert(subscribeMessage, 'default', DEFAULT_TIME_TO_SHOW_TOAST);

  onClick(e) {
    e.preventDefault();

    if (this.elementConfig.triggerBell) {
      window.CleverPush.triggerBellClick();
      return;
    }

    if (this.isSubscribed && this.elementConfig.buttonOnlyForTagsTopics) {
      this.onTargetingClick();
      return;
    }

    if (this.isSubscribed && !this.isMessengerWidget(MESSENGER_PLATFORM_KEYS.TELEGRAM) && !this.isMessengerWidget(MESSENGER_PLATFORM_KEYS.FACEBOOK)) {
      if (this.config.env !== 'PREVIEW') {
        this.api.trackWidget(this.elementConfig.id, TRACK_WIDGET_TYPES.OPT_OUT, this.testId);
      }
      this.subscriptionManager.unsubscribe(false).then(() => {

      });
      return;
    }

    // telegram
    if (this.isMessengerWidget(MESSENGER_PLATFORM_KEYS.TELEGRAM)) {
      if (this.config.env !== 'PREVIEW') {
        this.api.trackWidget(this.elementConfig.id, TRACK_WIDGET_TYPES.OPT_IN, this.testId, CHANNEL_TYPES.Telegram);
      }
      window.open(`https://t.me/${this.config.multiChannels.telegramChannel.telegramUsername}`);
      return;
    }

    // facebook
    if (this.isMessengerWidget(MESSENGER_PLATFORM_KEYS.FACEBOOK)) {
      if (this.config.env !== 'PREVIEW') {
        this.api.trackWidget(this.elementConfig.id, TRACK_WIDGET_TYPES.OPT_IN, this.testId, CHANNEL_TYPES.Facebook);
      }
      window.open(`https://m.me/${(this.config.multiChannels.facebookChannel.facebookPage || {}).id}`);
      return;
    }

    this.api.widgetId = this.id;

    if (this.subscriptionManager?.api && !this.subscriptionManager?.api.widgetId) {
      this.subscriptionManager.api.widgetId = this.id;
      this.subscriptionManager.api.widgetTestId = this.testId;
      this.subscriptionManager.api.widgetChannelId = this.config.channelId;
    }

    this.api.confirmAlertShown();

    this.subscriptionManager.setTopics(this.getSelectedTopics());

    this.button.innerHTML = '<div class="cleverpush-loader"><div></div><div></div><div></div><div></div></div>';

    let confirmAlertSelectTopicsLaterOriginal;
    if (this.config.confirmAlertSelectTopicsLater) {
      confirmAlertSelectTopicsLaterOriginal = true;
      this.config.confirmAlertSelectTopicsLater = false;
    }

    // initial tags + topics
    if (this.elementConfig.assignTags && this.elementConfig.tags && this.elementConfig.tags.length) {
      if (this.subscriptionManager && this.subscriptionManager.initialTags) {
        this.elementConfig.tags.forEach((tagId) => {
          this.subscriptionManager.initialTags.push(tagId);
        });
      }
    }
    if (this.elementConfig.assignTopics && this.elementConfig.topics && this.elementConfig.topics.length) {
      if (this.subscriptionManager && this.subscriptionManager.topics) {
        this.elementConfig.topics.forEach((topicId) => {
          this.subscriptionManager.topics.push(topicId);
        });
      }
    }

    this.subscriptionManager.subscribeForce(this.isSubscribed).then(() => {
      if (confirmAlertSelectTopicsLaterOriginal) {
        this.config.confirmAlertSelectTopicsLater = true;
      }

      if (this.config && this.config.widgets) {
        const currentWidget = this.config.widgets.find((widget) => widget._id === this.id);
        if (currentWidget?.subscribeMessage) {
          try {
            this.showSuccessMessageAlert(currentWidget.subscribeMessage);
          } catch (error) {
            console.log(error);
          }
        }
      }
    }).catch((err) => {
      if (this.config.ownDomain && location.protocol === 'https:' && (err.reason === 'denied' || err.reason === 'default')) {
        if (this.subscriptionManager.confirm) {
          this.subscriptionManager.confirm.showBackdrop(undefined, 'denied', err.message);
        }
      } else {
        log.error(err);
      }
      this.show(this.isSubscribed);
    });
  }

  isMessengerWidget(platformKey) {
    try {
      const { multiChannels, channelId } = this.config;
      const { channel: elementChannel, widgetType } = this.elementConfig;

      if (!multiChannels || !channelId || !elementChannel) {
        return false;
      }

      if (!multiChannels[platformKey] || !multiChannels[platformKey]._id) {
        return false;
      }

      if (multiChannels[platformKey]._id !== elementChannel) {
        return false;
      }

      return (multiChannels[platformKey]._id !== channelId || widgetType === MESSENGER_WIDGET_KEY);
    } catch (error) {
      return false;
    }
  }

  isValidSharedWidget(widget) {
    if (widget.type === CHANNEL_TYPES.App && ((os.name === 'Android' && widget.androidPackageName) || (os.name === 'iOS' && widget.iosStoreId))) {
      return true;
    }

    if (widget.type === CHANNEL_TYPES.Facebook && widget.facebookPage && widget.facebookPage.id) {
      return true;
    }

    if (widget.type === CHANNEL_TYPES.Telegram && widget.telegramUsername) {
      return true;
    }

    if (widget.type === CHANNEL_TYPES.Email) {
      return true;
    }

    return false;
  }

  hasSharedWidgetChannels() {
    const widgetSharedChannels = this.elementConfig?.widgetSharedChannels || [];
    return (widgetSharedChannels || []).some((widget) => SHARED_WIDGET_CHANNEL_TYPES.includes(widget.type) && this.isValidSharedWidget(widget));
  }

  onClickDeny(e) {
    e.preventDefault();

    if (this.elementConfig.previewTab) {
      return;
    }

    if (this.elementConfig.triggerBell) {
      window.CleverPush.triggerBellClick();
      return;
    }

    this.element.style.display = 'none';

    try {
      localStorage.setItem(`cleverpush-widget-status-${this.elementConfig.id}`, 'hidden');
    } catch (err) { }

    document.body.classList.remove('cleverpush-widget-popup-open');
  }

  onClickUnsubscribe(e) {
    e.preventDefault();

    if (this.config.env !== 'PREVIEW') {
      this.api.trackWidget(this.elementConfig.id, TRACK_WIDGET_TYPES.OPT_OUT, this.testId);
    }
    if (this.elementConfig.triggerBell) {
      window.CleverPush.triggerBellClick();
      return;
    }

    if (this.isSubscribed && this.elementConfig.buttonOnlyForTagsTopics) {
      this.onTargetingClick();
      return;
    }

    this.subscriptionManager.unsubscribe(false).then(() => {

    });
  }

  async saveTopics(e) {
    e.preventDefault();

    if (this.savingTopics) {
      return;
    }
    this.savingTopics = true;

    this.saveTopicsButton.innerHTML += '...';
    this.saveTopicsButton.disabled = true;
    if (this.element && this.element.dataset && this.element.dataset.buttonBackgroundColor) {
      this.saveTopicsButton.style.setProperty('border-color', this.element.dataset.buttonBackgroundColor, 'important');
    }

    const resetButton = (success) => {
      if (success) {
        this.saveTopicsButton.innerHTML = `${translate('panel.successfullySaved')}`;
        setTimeout(() => {
          this.saveTopicsButton.disabled = false;
          this.saveTopicsButton.innerHTML = translate('panel.save');
          this.savingTopics = false;
        }, 2000);
      } else {
        this.saveTopicsButton.disabled = false;
        this.saveTopicsButton.innerHTML = translate('panel.save');
        this.savingTopics = false;
      }
    };

    const topics = this.getSelectedTopics();
    try {
      const subscriptionTags = await this.subscriptionManager.getTags();
      if (this.isSubscribed && this.config.channelTags?.length && this.elementConfig.showSubscribedTags && subscriptionTags.length) {
        const removedTags = subscriptionTags.filter((tag) => !(this.getSelectedTags() || []).includes(tag));
        const newTags = this.getSelectedTags().filter((tag) => !subscriptionTags.includes(tag) && !removedTags.includes(tag));
        const subscriptionId = await this.subscriptionManager?.getSubscriptionId();
        if (removedTags.length) {
          await this.addOrRemoveTags(removedTags, subscriptionId, null, true);
        }
        if (newTags.length) {
          this.isSubscribedTargeting = false;
          await this.addOrRemoveTags(newTags, subscriptionId);
        }
      }
      await this.subscriptionManager.setTopics(topics);
      await this.subscriptionManager.sync(false, topics);
      resetButton(true);
    } catch (err) {
      resetButton(false);
    }
  }
}
